import type { ReactElement } from "react"
import Header from "@gatsby-plugin-generic-page/components/Header"
import type { Header as HeaderType } from "@gatsby-plugin-generic-page/fragments/header"
import type { HeaderAlign } from "@gatsby-plugin-generic-page/fragments/headerAlign"
import type { HeaderAlignHorizontal } from "@gatsby-plugin-generic-page/fragments/headerAlignHorizontal"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"
import {
  DEFAULT_HORIZONTAL_ALIGN,
  HORIZONTAL_ALIGN_CLASS,
  HORIZONTAL_ALIGN_LG_CLASS,
  HORIZONTAL_ALIGN_MD_CLASS,
  VERTICAL_ALIGN_LG_CLASS,
  VERTICAL_ALIGN_MD_CLASS,
} from "./classNames"

export type AlignedHeaderProps = {
  header: HeaderType
  /** Mobile carries no vertical alignment — the header always stacks here. */
  alignMobile?: HeaderAlignHorizontal
  alignTablet?: HeaderAlign
  alignDesktop?: HeaderAlign
  /**
   * Emit the vertical `self-*` classes. Only meaningful when the header is a
   * child of a horizontal flex row — in a stacked column `self-*` would
   * control horizontal position and contradict `horizontalAlign`.
   */
  hasVerticalAlign?: boolean
  className?: string
}

function AlignedHeader({
  header,
  alignMobile,
  alignTablet,
  alignDesktop,
  hasVerticalAlign = false,
  className,
}: AlignedHeaderProps): ReactElement<AlignedHeaderProps> {
  return (
    <div
      className={cn(
        "flex flex-col",
        HORIZONTAL_ALIGN_CLASS[
          alignMobile?.horizontalAlign ?? DEFAULT_HORIZONTAL_ALIGN
        ],
        !isEmpty(alignTablet?.horizontalAlign) &&
          HORIZONTAL_ALIGN_MD_CLASS[alignTablet.horizontalAlign],
        !isEmpty(alignDesktop?.horizontalAlign) &&
          HORIZONTAL_ALIGN_LG_CLASS[alignDesktop.horizontalAlign],
        hasVerticalAlign &&
          !isEmpty(alignTablet?.verticalAlign) &&
          VERTICAL_ALIGN_MD_CLASS[alignTablet.verticalAlign],
        hasVerticalAlign &&
          !isEmpty(alignDesktop?.verticalAlign) &&
          VERTICAL_ALIGN_LG_CLASS[alignDesktop.verticalAlign],
        className
      )}
    >
      <Header {...header} />
    </div>
  )
}

export default AlignedHeader
